import { router, usePage } from "@inertiajs/react";
import { type ModalResponseData, useModalStack } from "@inertiaui/modal-react";
import { useEffect, useRef } from "react";

interface ModalPageProps {
  _inertiaui_modal?: ModalResponseData & { baseUrl?: string };
  [key: string]: unknown;
}

/**
 * Opens a routed modal on a *direct* visit to its URL (hard load, refresh, or a
 * shared/bookmarked link). On such a visit the server (inertia_rails-contrib)
 * renders the modal's `base_url` page and ships the modal payload in
 * `_inertiaui_modal`. @inertiaui/modal's own `ModalRoot` only auto-opens modals
 * from the router's `navigate` event — which never fires for the *initial*
 * page — so its `_inertiaui_modal` effect only updates an already-open modal and
 * a deep-linked one would otherwise never open. This bridges that single gap.
 *
 * In-app `visitModal()` opens are untouched: they push onto the stack (so
 * `stack.length > 0` here) and are handled by ModalRoot's navigate listener.
 * Mount this once alongside `<ModalRoot/>`.
 */
export function RoutedModalDeepLink() {
  const { stack, pushFromResponseData } = useModalStack();
  const modal = usePage<ModalPageProps>().props._inertiaui_modal;
  const stackSize = stack.length;
  // Open at most once per mounted layout — guards against the (rare) race with
  // ModalRoot's navigate listener also firing for the same initial page.
  const handledRef = useRef(false);

  useEffect(() => {
    if (handledRef.current || !modal || stackSize > 0) return;
    handledRef.current = true;
    pushFromResponseData(modal, {}, () => {
      // Closing leaves the modal route for its base page so the URL drops the
      // modal segment (e.g. /@handle/passport -> /@handle).
      if (modal.baseUrl) {
        router.visit(modal.baseUrl, { preserveScroll: true, preserveState: true });
      }
    });
  }, [modal, stackSize, pushFromResponseData]);

  return null;
}
